/**
 * Состояния списка: загрузка, ошибка, пустота.
 *
 * Dialogue, ShadowDialogue, InboxList и страницы верстали их заново — одна
 * и та же разметка расходилась по классам и текстам. Здесь дублировалось
 * не бизнес-правило, а отсутствующий компонент.
 */

import type { ReactNode } from 'react';
import { Spinner } from './Spinner';

interface StateProps {
  /** Заполнить всю высоту родителя (для панелей диалога) */
  fullHeight?: boolean;
  className?: string;
}

const box = (fullHeight: boolean, extra = ''): string =>
  `flex items-center justify-center ${fullHeight ? 'h-full' : 'py-8'} ${extra}`.trim();

/** Идёт загрузка — данных ещё нет */
export function LoadingState({ fullHeight = true, className = '' }: StateProps): JSX.Element {
  return (
    <div className={`${box(fullHeight)} ${className}`.trim()}>
      <Spinner size="lg" className="text-text-secondary" />
    </div>
  );
}

interface ErrorStateProps extends StateProps {
  error: Error | string;
}

/** Запрос упал — показываем причину */
export function ErrorState({ error, fullHeight = true, className = '' }: ErrorStateProps): JSX.Element {
  const message = typeof error === 'string' ? error : error.message;
  return (
    <div className={`${box(fullHeight, 'text-accent-danger')} ${className}`.trim()}>
      Ошибка: {message}
    </div>
  );
}

interface EmptyStateProps extends StateProps {
  /** Иконка в круге — обычно svg 8x8 */
  icon?: ReactNode;
  title: string;
  hint?: string;
}

/** Данных нет — и это нормально */
export function EmptyState({
  icon,
  title,
  hint,
  fullHeight = true,
  className = '',
}: EmptyStateProps): JSX.Element {
  return (
    <div
      className={`flex flex-col items-center justify-center ${
        fullHeight ? 'h-full' : 'py-8'
      } text-text-secondary p-4 ${className}`.trim()}
    >
      {icon && (
        <div className="w-16 h-16 rounded-full bg-surface-subtle flex items-center justify-center mb-4">
          {icon}
        </div>
      )}
      <p className="text-sm text-center">{title}</p>
      {hint && <p className="text-xs text-center mt-1 opacity-70">{hint}</p>}
    </div>
  );
}
